import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals';
import { getEnv } from '@rtvloco/platform/env';

/**
 * Initialize browser Real-User-Monitoring (web-vitals) for this SPA. Idempotent
 * — safe to call multiple times. NEXT-498 (PHAP Track E): feeds the
 * `portal_render_latency` SLO, whose PromQL (governance/slos.yaml) reads
 * `portal_render_duration_seconds_bucket` — a histogram recorded server-side
 * by portal-bff from the `LCP` samples this module reports.
 *
 * MUST be called AFTER `await loadConfig()` so /config.json values are
 * resolvable via getEnv(). No direct window.__ENV__ or import.meta.env
 * access — getEnv() owns that resolution (same rule as observability/tracing.ts).
 *
 * Collector URL resolution mirrors observability/tracing.ts's collectorUrl:
 *   In-cluster: '/rum/v1/vitals' (nginx proxies /rum/ → portal-bff)
 *   Local: absent — RUM is a no-op when unset, exactly like OTel tracing.
 */
export function initAppRum(): void {
  if (initialized) return;
  initialized = true;

  try {
    initAppRumUnsafe();
  } catch (err) {
    // console.debug (not warn): matches the "RUM disabled" case below in
    // severity -- a lost RUM init is a best-effort diagnostic, not a
    // console-log security-policy violation the app needs to surface.
    console.debug('[RUM] web-vitals init failed — app continues without RUM', err);
  }
}

let initialized = false;

function initAppRumUnsafe(): void {
  const collectorUrl = getEnv('rumCollectorUrl', '/rum/v1/vitals');
  if (!collectorUrl) {
    console.debug('[RUM] No rumCollectorUrl configured — RUM disabled');
    return;
  }

  const report = (metric: Metric) => sendSample(collectorUrl, metric);

  onLCP(report);
  onCLS(report);
  onINP(report);
  onFCP(report);
  onTTFB(report);
}

/**
 * Fire-and-forget beacon send. `sendBeacon` is preferred — it survives page
 * unload, which matters because LCP/CLS/INP often finalize right as the user
 * navigates away. `fetch(..., {keepalive: true})` is the fallback for
 * environments without `sendBeacon` (or a beacon rejected because the
 * browser's queued-bytes budget is full). A lost RUM sample is not worth
 * retrying — this must never block or delay the page.
 */
function sendSample(collectorUrl: string, metric: Metric): void {
  const body = JSON.stringify({ name: metric.name, value: metric.value, id: metric.id });

  if (typeof navigator.sendBeacon === 'function') {
    const sent = navigator.sendBeacon(collectorUrl, new Blob([body], { type: 'application/json' }));
    if (sent) return;
  }

  fetch(collectorUrl, {
    method: 'POST',
    body,
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
  }).catch(() => {
    // best-effort telemetry — a dropped sample is not an app error
  });
}
